/**
 * Recently Viewed Component
 * Displays user's recently viewed content
 */

'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Clock, ChevronRight, History, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '@/lib/auth/AuthContext';

interface RecentlyViewedItem {
  id: number;
  contentType: string;
  contentSlug: string;
  contentTitle: string | null;
  viewedAt: string;
  viewCount: number;
}

interface RecentlyViewedProps {
  limit?: number;
  showHeader?: boolean;
  contentTypes?: string[];
  variant?: 'list' | 'horizontal' | 'compact';
  className?: string;
}

const contentTypeConfig: Record<
  string,
  { label: string; color: string; path: string }
> = {
  insight: { label: 'Insight', color: 'bg-accent-muted text-accent', path: '/insights' },
  framework: { label: 'Framework', color: 'bg-purple-500/20 text-purple-400', path: '/frameworks' },
  tool: { label: 'Tool', color: 'bg-teal-500/20 text-teal-400', path: '/tools' },
  comparison: { label: 'Comparison', color: 'bg-blue-500/20 text-blue-400', path: '/comparisons' },
  code: { label: 'Code', color: 'bg-pink-500/20 text-pink-400', path: '/code' },
  architecture: { label: 'Architecture', color: 'bg-indigo-500/20 text-indigo-400', path: '/architectures' },
  template: { label: 'Template', color: 'bg-green-500/20 text-green-400', path: '/templates' },
  question: { label: 'Question', color: 'bg-yellow-500/20 text-yellow-400', path: '/questions' },
  'learning-path': { label: 'Learning Path', color: 'bg-cyan-500/20 text-cyan-400', path: '/learning-paths' },
};

export default function RecentlyViewed({
  limit = 10,
  showHeader = true,
  contentTypes,
  variant = 'list',
  className = '',
}: RecentlyViewedProps) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<RecentlyViewedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    const fetchRecentlyViewed = async () => {
      try {
        const params = new URLSearchParams({ limit: limit.toString() });
        if (contentTypes && contentTypes.length > 0) {
          params.set('type', contentTypes[0]); // API only supports one type at a time
        }

        const response = await fetch(`/api/secure/recently-viewed?${params}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch recently viewed');
        }

        const data = await response.json();
        setItems(data.data?.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentlyViewed();
  }, [isAuthenticated, limit, contentTypes]);

  const clearHistory = async () => {
    try {
      await fetch('/api/secure/recently-viewed', {
        method: 'DELETE',
        credentials: 'include',
      });
      setItems([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  const getRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getContentUrl = (item: RecentlyViewedItem): string => {
    const config = contentTypeConfig[item.contentType];
    return config ? `${config.path}/${item.contentSlug}` : `/${item.contentSlug}`;
  };

  if (!isAuthenticated) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <Loader2 className="w-6 h-6 text-foreground-tertiary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-8 text-red-400 ${className}`}>
        <p>{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <History className="w-12 h-12 text-foreground-muted mx-auto mb-3" />
        <p className="text-foreground-tertiary text-sm">No recently viewed content</p>
        <p className="text-foreground-muted text-xs mt-1">
          Your browsing history will appear here
        </p>
      </div>
    );
  }

  // Horizontal scroll variant
  if (variant === 'horizontal') {
    return (
      <div className={className}>
        {showHeader && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent" />
              Recently Viewed
            </h3>
            <button
              onClick={clearHistory}
              className="text-foreground-tertiary hover:text-foreground text-sm flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>
          </div>
        )}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
          {items.map((item) => {
            const config = contentTypeConfig[item.contentType];
            return (
              <Link
                key={item.id}
                href={getContentUrl(item)}
                className="flex-shrink-0 w-48 bg-surface rounded-lg border border-border-muted p-3 hover:border-border transition-colors"
              >
                <span
                  className={`inline-block text-xs px-2 py-0.5 rounded-full ${config?.color || 'bg-cream/10 text-foreground-secondary'} mb-2`}
                >
                  {config?.label || item.contentType}
                </span>
                <p className="text-foreground text-sm font-medium line-clamp-2">
                  {item.contentTitle || item.contentSlug}
                </p>
                <p className="text-foreground-tertiary text-xs mt-1">
                  {getRelativeTime(item.viewedAt)}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <div className={className}>
        {items.slice(0, 5).map((item) => {
          const config = contentTypeConfig[item.contentType];
          return (
            <Link
              key={item.id}
              href={getContentUrl(item)}
              className="flex items-center gap-2 py-2 hover:bg-cream/5 -mx-2 px-2 rounded transition-colors group"
            >
              <span
                className={`w-2 h-2 rounded-full ${config?.color?.replace('text-', 'bg-').split(' ')[0] || 'bg-cream/30'}`}
              />
              <span className="flex-1 text-sm text-foreground-secondary group-hover:text-foreground truncate">
                {item.contentTitle || item.contentSlug}
              </span>
              <span className="text-xs text-foreground-tertiary">
                {getRelativeTime(item.viewedAt)}
              </span>
            </Link>
          );
        })}
      </div>
    );
  }

  // List variant (default)
  return (
    <div className={className}>
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Clock className="w-5 h-5 text-accent" />
            Recently Viewed
          </h3>
          <button
            onClick={clearHistory}
            className="text-foreground-tertiary hover:text-foreground text-sm flex items-center gap-1"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        </div>
      )}
      <div className="space-y-2">
        {items.map((item) => {
          const config = contentTypeConfig[item.contentType];
          return (
            <Link
              key={item.id}
              href={getContentUrl(item)}
              className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-border-muted hover:border-border transition-colors group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${config?.color || 'bg-cream/10 text-foreground-secondary'}`}
                  >
                    {config?.label || item.contentType}
                  </span>
                  {item.viewCount > 1 && (
                    <span className="text-xs text-foreground-muted">
                      Viewed {item.viewCount}x
                    </span>
                  )}
                </div>
                <p className="text-foreground font-medium truncate group-hover:text-accent transition-colors">
                  {item.contentTitle || item.contentSlug}
                </p>
              </div>
              <div className="flex items-center gap-2 text-foreground-tertiary">
                <span className="text-xs">{getRelativeTime(item.viewedAt)}</span>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
